/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.3
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo } from "react";

// react-router-dom components
import { Link } from "react-router-dom";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Doughnut } from "react-chartjs-2";

// @mui material components
import Card from "@mui/material/Card";
import Tooltip from "@mui/material/Tooltip";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

import ComplexReportsDoughnutChartItem from "./ComplexReportsDoughnutChartItem";
import configs from "./configs";
import { useSystemTheme } from "context/SystemThemeContext";

function ComplexReportsDoughnutChart({ title, chart, tooltip = "", action = false }) {
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";
  
  const { data, options } = configs(chart.labels || [], chart.datasets || {});

  // Update chart options for dark mode
  const getChartOptions = () => {
    const baseOptions = options || {};
    
    return {
      ...baseOptions,
      plugins: {
        ...baseOptions.plugins,
        legend: {
          ...baseOptions.plugins?.legend,
          labels: {
            ...baseOptions.plugins?.legend?.labels,
            color: isDarkMode ? "#e2e8f0" : undefined,
          },
        },
        tooltip: {
          ...baseOptions.plugins?.tooltip,
          backgroundColor: isDarkMode ? "#060C18" : "#fff",
          titleColor: isDarkMode ? "#e2e8f0" : "#1a202c",
          bodyColor: isDarkMode ? "#a0aec0" : "#67748e",
          borderColor: isDarkMode ? "#2d2d3f" : "#e0e0e0",
          borderWidth: 1,
        },
      },
    };
  };

  const renderItems = chart.labels
    ? chart.labels.map((label, key) => (
      <ComplexReportsDoughnutChartItem
        image={chart.images && chart.images[key]}
        title={label}
        key={label}
        percentage={`${chart.datasets && chart.datasets.data ? chart.datasets.data[key] : 0}`}
        hasBorder={key !== chart.labels.length - 1}
      />
    ))
    : null;

  const renderButton = () => {
    let template;

    if (action) {
      template =
        action.type === "internal" ? (
          <SoftBox mt={3} mb={2}>
            <SoftButton
              component={Link}
              to={action.route}
              variant="gradient"
              color={action.color}
              size="small"
            >
              {action.label}
            </SoftButton>
          </SoftBox>
        ) : (
          <SoftBox mt={3} mb={2}>
            <SoftButton
              component="a"
              href={action.route}
              target="_blank"
              rel="noreferrer"
              variant="gradient"
              color={action.color}
              size="small"
            >
              {action.label}
            </SoftButton>
          </SoftBox>
        );
    }

    return template;
  };

  return (
    <Card sx={{ 
      height: "100%",
      backgroundColor: isDarkMode ? "#060C18" : "#ffffff",
      transition: "background-color 0.3s ease",
      border: isDarkMode ? "1px solid #2d2d3f" : "none",
    }}>
      <SoftBox display="flex" justifyContent="space-between" alignItems="center" pt={2} px={2}>
        <SoftTypography 
          variant="h6" 
          sx={{ color: isDarkMode ? "#e2e8f0" : undefined }}
        >
          {title}
        </SoftTypography>
        <Tooltip title={tooltip} placement="right">
          <SoftButton 
            variant="outlined" 
            color="secondary" 
            size="small" 
            circular 
            iconOnly
            sx={{
              borderColor: isDarkMode ? "#4a5568" : undefined,
              color: isDarkMode ? "#a0aec0" : undefined,
              "&:hover": {
                borderColor: "#219297",
                color: "#219297",
                backgroundColor: isDarkMode ? "#2d2d3f" : undefined,
              },
            }}
          >
            <Icon>priority_high</Icon>
          </SoftButton>
        </Tooltip>
      </SoftBox>
      
      <SoftBox position="relative" p={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={5} sx={{ textAlign: "center" }}>
            <SoftBox height="100%" display="flex" flexDirection="column">
              <SoftBox height="100%" mt={5} mx={1}>
                {useMemo(
                  () => (
                    <Doughnut data={data} options={getChartOptions()} />
                  ),
                  [chart, isDarkMode]
                )}
              </SoftBox>
              {renderButton()}
            </SoftBox>
          </Grid>
          <Grid item xs={12} lg={7}>
            {useMemo(() => renderItems, [chart])}
          </Grid>
        </Grid>
      </SoftBox>
    </Card>
  );
}

// Typechecking props for the ComplexReportsDoughnutChart
ComplexReportsDoughnutChart.propTypes = {
  title: PropTypes.string.isRequired,
  chart: PropTypes.shape({
    images: PropTypes.arrayOf(PropTypes.string),
    labels: PropTypes.arrayOf(PropTypes.string).isRequired,
    datasets: PropTypes.objectOf(
      PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.string])
    ).isRequired,
  }).isRequired,
  tooltip: PropTypes.string,
  action: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      type: PropTypes.oneOf(["external", "internal"]).isRequired,
      route: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.oneOf([
        "primary",
        "secondary",
        "info",
        "success",
        "warning",
        "error",
        "dark",
        "light",
        "green",
        "teal"
      ]),
    }),
  ]),
};

export default ComplexReportsDoughnutChart;